import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

import { StatusLiveRegion } from './status-live-region';

/**
 * How long an announcement stays in the app's status live region before it is cleared (WCAG
 * 4.1.3). Without a duration the announcer keeps its last text for good, so a screen-reader user
 * browsing to the end of the page with the virtual cursor still met "loading" long after the wait
 * was over. Clearing the element does not cut off speech already queued; it only stops the text
 * being found again later.
 */
export const STATUS_ANNOUNCE_CLEAR_MS = 3000;

/** The same, for the longer sentences (the kiosk wizard names every site-data area that refused). */
export const STATUS_ANNOUNCE_LONG_CLEAR_MS = 8000;

/**
 * How long an OUTCOME holds off a later PROGRESS message once it is SET. The live region replaces a
 * pending text on the next call (it sets the text 100ms after it), so a "loading" from a component
 * the outcome's own navigation creates silently replaced "added to cart": the cart add navigates
 * `cart/add` -> `cart/:id`, the router builds a new stock list, and its `ngOnInit` announced its
 * configuration wait inside those 100ms (MR !197 review, round 10). A wait is worth less than the
 * result the user is waiting to hear, so the wait is the one dropped.
 *
 * While the outcome is still PENDING a progress message is dropped however long that takes: a timer
 * Safari froze in a background tab outlived this hold, and the next "loading" cancelled the outcome
 * with nothing to say it again (MR !197 review, round 18).
 */
export const OUTCOME_HOLD_MS = 1500;

/*
 * Two OUTCOMES are joined while the first is still PENDING -- handed to the region but not yet set.
 * The region sets its text 100ms after the call and a later call replaces a pending text, so a second
 * outcome inside the delay would silently replace the first: the wizard's "iPad added." is followed
 * by the navigation to `provisioning/:id`, whose new instance syncs its own announcements at once
 * (MR !197 review, round 11). The region's promise resolves the moment the text is set, which is
 * exactly the boundary (round 12).
 */

export interface StatusAnnounceOptions {
  /** `true`: the long clear duration, for long sentences. */
  long?: boolean;
  /**
   * `'progress'`: a wait ("loading") -- dropped while an outcome is still being read.
   * `'outcome'` (default): the result of something the user did.
   */
  kind?: 'progress' | 'outcome';
}

/** Whether the region SET the text (`false`: replaced by a later call of ours first). */
type HandResult = Promise<boolean>;

/** A text handed to the region and not yet set; `set` is the promise of {@link HandResult}. */
interface PendingText {
  kind: 'progress' | 'outcome';
  text: string;
  long: boolean;
  set?: HandResult;
}

/**
 * A wait announced through {@link StatusAnnouncerService.beginWait}. Its close ("loaded") is said
 * only if its "loading" was actually SET in the live region -- never retracted, never decided by
 * text. A later message replacing a pending "loading" means the user never heard the wait, so its
 * close would announce the end of something they were never told began (MR !197 review, round 16).
 * A handle belongs to the one wait that made it, so one caller never closes another's identical
 * "loading".
 */
export class StatusWait {
  /** `'pending'` until the region answers; `'dropped'` when never handed (held off, or not translatable). */
  private state: 'pending' | 'spoken' | 'lost' | 'dropped';
  private close: { key: string; params?: object } | null = null;
  private settled = false;

  /** @internal Built by the service only. */
  constructor(
    private readonly service: StatusAnnouncerService,
    set: HandResult | null,
  ) {
    if (set === null) {
      this.state = 'dropped';
      return;
    }
    this.state = 'pending';
    void set.then((spoken) => {
      this.state = spoken ? 'spoken' : 'lost';
      if (spoken && this.close !== null) {
        const close = this.close;
        this.close = null;
        this.service.announceClose(close.key, close.params);
      }
      this.close = null;
    });
  }

  /**
   * The wait is over. `closeKey` (if any) is announced only once the "loading" is known to have been
   * heard: at once if the region already set it, when it sets it if it is still pending, never if it
   * was replaced or never handed. Idempotent.
   */
  settle(closeKey?: string, params?: object): void {
    if (this.settled) {
      return;
    }
    this.settled = true;
    if (closeKey === undefined) {
      return;
    }
    if (this.state === 'spoken') {
      this.service.announceClose(closeKey, params);
    } else if (this.state === 'pending') {
      this.close = { key: closeKey, params };
    }
  }

  /** The waiting screen is gone: a close still deferred is not said. */
  abandon(): void {
    this.settled = true;
    this.close = null;
  }
}

/**
 * The ONE way this app announces a status message (WCAG 4.1.3): a polite live region this service
 * owns ({@link StatusLiveRegion}), which exists before every message and clears-then-sets, so a
 * repeat is read again -- always with a clear duration, which no caller can now forget, and with
 * outcomes protected from a later wait. Nothing else writes that region, so whether a text was set
 * is known from this service's own state rather than read back out of somebody else's element.
 */
@Injectable({ providedIn: 'root' })
export class StatusAnnouncerService {
  private lastOutcomeAt = Number.NEGATIVE_INFINITY;
  /** The text the region still has pending (handed, not yet set), or `null` when nothing is. */
  private pending: PendingText | null = null;
  /** Bumped per hand, so a stale promise never clears the pending state of a newer text. */
  private handSeq = 0;

  constructor(
    private region: StatusLiveRegion,
    private translate: TranslateService,
    private zone: NgZone,
  ) {}

  /** Announce an already-translated text. A progress message held off by an outcome is dropped. */
  announce(text: string, options: StatusAnnounceOptions = {}): void {
    this.hand(text, options);
  }

  /**
   * Announce the translation of `key`. Pass a literal key, so `audit:i18n` check 3 resolves it.
   *
   * ngx-translate ECHOES a key it cannot resolve, and `instant` cannot resolve anything while the
   * language file is still loading -- which is when the app-update banner goes up after a boot-time
   * chunk error, so the dotted key itself was read aloud (MR !197 review, round 13). A miss goes
   * through `get`, which waits for a load in flight and otherwise answers at once (synchronously, so
   * ordering against other calls is unchanged). A key still missing after that is a missing key,
   * which `audit:i18n` check 3 exists to refuse -- and it is not read aloud either (round 15).
   *
   * A PROGRESS message whose text is not loaded yet is not announced at all: by the time the file
   * lands an outcome may be holding progress off (round 14). An OUTCOME is never held off, so a
   * deferred one still goes out.
   */
  announceKey(key: string, params?: object, options: StatusAnnounceOptions = {}): void {
    const outcome = (options.kind ?? 'outcome') === 'outcome';
    this.withText(key, params, outcome, (text) => {
      this.hand(text, options);
    });
  }

  /**
   * Announce a wait ("loading", by `key`) and get the handle that closes it. See {@link StatusWait}.
   * A wait whose text is not loaded yet is dropped, exactly as a progress `announceKey` is.
   */
  beginWait(key: string, params?: object): StatusWait {
    let set: HandResult | null = null;
    this.withText(key, params, false, (text) => {
      set = this.hand(text, { kind: 'progress' });
    });
    return new StatusWait(this, set);
  }

  /**
   * @internal The close of a {@link StatusWait} whose "loading" WAS heard. A progress message, so it
   * never replaces an outcome -- but unlike every other progress message it is not DROPPED by one:
   * it waits for the outcome to be set and for the hold to run out, then goes out. Dropping it broke
   * the one promise a wait makes: an outcome announced within the hold left the user with a spoken
   * "loading" and no end (MR !197 review, round 19).
   */
  announceClose(key: string, params?: object): void {
    this.withText(key, params, false, (text) => this.deliverClose(text));
  }

  private deliverClose(text: string): void {
    const pending = this.pending;
    if (pending?.kind === 'outcome' && pending.set) {
      void pending.set.then(() => this.deliverClose(text));
      return;
    }
    const held = OUTCOME_HOLD_MS - (Date.now() - this.lastOutcomeAt);
    if (held > 0) {
      // Outside the zone: the callback only writes the live region, which is not Angular's to render,
      // and an in-zone macrotask of up to OUTCOME_HOLD_MS held `isStable` false and bought an
      // app-wide change-detection pass for nothing (MR !197 review, round 21).
      this.zone.runOutsideAngular(() => setTimeout(() => this.deliverClose(text), held));
      return;
    }
    this.hand(text, { kind: 'progress' });
  }

  /**
   * `key`'s translation for a caller that composes its own sentence before {@link announce}, or
   * `null` when it must not be read aloud: an echoed key once a language is selected (missing, or
   * its file still loading -- `instant` cannot wait), or a namespace object. Composing with a bare
   * `translate.instant` read the dotted key aloud on exactly those misses (round 16).
   *
   * "Selected", not "loaded": ngx-translate 14's `use()` sets `currentLang` BEFORE the file arrives,
   * so a cold load straight into the kiosk wizard had a language with no translations, read that as
   * "no file at all", and answered the dotted key -- which the wizard's tracker then recorded as said
   * and never retried (MR !197 review, round 17). A `null` is not recorded, so the message is tried
   * again on the next sync.
   */
  translateNow(key: string, params?: object): string | null {
    const text: unknown = this.translate.instant(key, params);
    if (typeof text !== 'string') {
      return null;
    }
    // With no language at all (a TestBed without a loader) a missing key cannot be told from any
    // other, and the echo is what is said -- the same rule `announceKey` applies.
    if (text === key && this.languageSelected()) {
      return null;
    }
    return text;
  }

  /**
   * The language messages are being announced in: the one in use, else the default, else `null`.
   * One answer for every caller that has to compose language-dependent text (the wizard's list
   * conjunction) -- a second copy of "which language is active" can drift from this one.
   */
  activeLanguage(): string | null {
    return this.translate.currentLang || this.translate.defaultLang || null;
  }

  /**
   * Resolve `key` and hand the text to `deliver` -- at once, or (only when `deferOk`) once a
   * language file still loading has arrived.
   */
  private withText(key: string, params: object | undefined, deferOk: boolean, deliver: (text: string) => void): void {
    const text: unknown = this.translate.instant(key, params);
    if (typeof text === 'string' && text !== key) {
      deliver(text);
      return;
    }
    // A key naming a NAMESPACE answers its object, which would be read as "[object Object]"
    // (MR !197 review, round 15). Never a message.
    if (text !== key) {
      return;
    }
    let returned = false;
    this.translate
      .get(key, params)
      .pipe(take(1))
      .subscribe({
        next: (later: unknown) => {
          // An object is a namespace key, and the key itself once a language is selected is a
          // missing key -- or a file that FAILED to load, which leaves the language with no
          // translations and `get` answering the bare key at once: neither is read aloud. "Selected",
          // not "loaded", for the same reason as `translateNow` (one rule for both resolvers): asking
          // "loaded" read the key aloud on exactly that failed load (MR !197 review, round 20). With
          // no language at all (a TestBed without a loader) a missing key cannot be told from any
          // other, and the echo is what is said.
          if (typeof later !== 'string' || (later === key && this.languageSelected())) {
            return;
          }
          // `get` answers synchronously when nothing is loading: then the result is known now.
          if (!returned || deferOk) {
            deliver(later);
          }
        },
        // The language file failed to load -- the deferred path runs exactly when the network may
        // be down (the app-update banner after a boot-time chunk error). There is no text to say:
        // `instant` already answered the bare key, which is never read aloud. Unhandled, the error
        // went to the global ErrorHandler and the caller's code path with it (MR !197 review,
        // round 19).
        error: () => undefined,
      });
    returned = true;
  }

  /**
   * Whether a language is in use at all, its file loaded, still loading or failed. The ONE test both
   * resolvers apply to an echoed key (`withText` and `translateNow`).
   */
  private languageSelected(): boolean {
    return this.activeLanguage() !== null;
  }

  /** Hand `text` to the region. `null` when dropped; otherwise a promise of whether it was SET. */
  private hand(text: string, options: StatusAnnounceOptions): HandResult | null {
    const kind = options.kind ?? 'outcome';
    const now = Date.now();
    if (kind === 'progress' && (this.pending?.kind === 'outcome' || now - this.lastOutcomeAt < OUTCOME_HOLD_MS)) {
      return null;
    }
    let long = options.long ?? false;
    if (kind === 'outcome') {
      // The previous outcome is still pending and this call would replace it: say both. An identical
      // text is not joined onto itself -- replacing it with itself loses nothing.
      if (this.pending?.kind === 'outcome' && this.pending.text !== text) {
        text = `${this.pending.text} ${text}`;
        long = long || this.pending.long;
      }
      this.lastOutcomeAt = now;
    }
    const seq = ++this.handSeq;
    const pending: PendingText = { kind, text, long };
    this.pending = pending;
    const duration = long ? STATUS_ANNOUNCE_LONG_CLEAR_MS : STATUS_ANNOUNCE_CLEAR_MS;
    pending.set = Promise.resolve(this.region.announce(text, duration)).then((set) => {
      if (seq === this.handSeq) {
        this.pending = null;
        if (kind === 'outcome') {
          // The hold runs from the moment the outcome is SET, not from when it was handed.
          this.lastOutcomeAt = Date.now();
        }
      }
      // A spy that resolves nothing counts as set: only an explicit `false` is a replacement.
      return set !== false;
    });
    return pending.set;
  }
}
